// Fees service layer for Loan Estimate-style fee management
// Handles manual fee entry and LOS fee integration
// Will later integrate with LOS APIs (e.g., Vesta)

/**
 * Fee categories matching Loan Estimate sections
 */
export const FEE_CATEGORIES = {
  ORIGINATION: 'Origination Charges',
  CANNOT_SHOP: 'Services Borrower Cannot Shop For',
  CAN_SHOP: 'Services Borrower Can Shop For',
  TAXES_GOV: 'Taxes and Government Fees',
  PREPAIDS: 'Prepaids',
  ESCROW: 'Initial Escrow Payment at Closing',
  OTHER: 'Other'
};

/**
 * Fee payer types
 */
export const FEE_PAYER = {
  BORROWER: 'Borrower',
  LENDER: 'Lender',
  SELLER: 'Seller',
  BROKER: 'Broker'
};

/**
 * Fee source types
 */
export const FEE_SOURCE = {
  MANUAL: 'Manual',
  LOS: 'LOS',
  COMP_PLAN: 'Comp Plan'
};

/**
 * Generates mock fee worksheet with realistic LE-style fees
 * @param {Object} loanData - Loan data
 * @returns {Object} Fee worksheet
 */
export const generateMockFeeWorksheet = (loanData) => {
  const loanAmount = loanData.loanAmount || 450000;
  const propertyValue = loanData.propertyValue || 500000;
  const downPayment = propertyValue - loanAmount;
  
  const fees = {
    // A. Origination Charges
    origination: [
      {
        id: 'broker-comp',
        name: 'Broker Compensation',
        amount: loanAmount * 0.02, // 2% of loan amount
        payer: FEE_PAYER.LENDER,
        source: FEE_SOURCE.COMP_PLAN,
        category: FEE_CATEGORIES.ORIGINATION,
        editable: false,
        description: 'Calculated from compensation plan: 2.00% of loan amount'
      },
      {
        id: 'lender-origination',
        name: 'Lender Origination Fee',
        amount: 995,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.ORIGINATION,
        editable: true,
        description: 'Lender processing fee'
      },
      {
        id: 'discount-points',
        name: 'Discount Points',
        amount: 0,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.MANUAL,
        category: FEE_CATEGORIES.ORIGINATION,
        editable: true,
        description: 'Optional - reduces interest rate'
      }
    ],
    
    // B. Services Borrower Cannot Shop For
    cannotShop: [
      {
        id: 'appraisal',
        name: 'Appraisal Fee',
        amount: 550,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.CANNOT_SHOP,
        editable: true,
        description: 'Property valuation'
      },
      {
        id: 'credit-report',
        name: 'Credit Report Fee',
        amount: 75,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.CANNOT_SHOP,
        editable: true,
        description: 'Tri-merge credit report'
      },
      {
        id: 'flood-cert',
        name: 'Flood Certification',
        amount: 25,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.CANNOT_SHOP,
        editable: true,
        description: 'Flood zone determination'
      },
      {
        id: 'tax-service',
        name: 'Tax Service Fee',
        amount: 85,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.CANNOT_SHOP,
        editable: true,
        description: 'Tax monitoring service'
      }
    ],
    
    // C. Services Borrower Can Shop For
    canShop: [
      {
        id: 'title-search',
        name: 'Title Search',
        amount: 350,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.CAN_SHOP,
        editable: true,
        description: 'Title examination'
      },
      {
        id: 'title-insurance',
        name: 'Lender\'s Title Insurance',
        amount: 1250,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.CAN_SHOP,
        editable: true,
        description: 'Protects lender interest'
      },
      {
        id: 'settlement-fee',
        name: 'Settlement/Closing Fee',
        amount: 450,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.CAN_SHOP,
        editable: true,
        description: 'Escrow/closing agent fee'
      },
      {
        id: 'survey',
        name: 'Survey Fee',
        amount: 400,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.MANUAL,
        category: FEE_CATEGORIES.CAN_SHOP,
        editable: true,
        description: 'Property boundary survey'
      }
    ],
    
    // D. Taxes and Government Fees
    taxesGov: [
      {
        id: 'recording-deed',
        name: 'Recording Fee - Deed',
        amount: 125,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.TAXES_GOV,
        editable: true,
        description: 'County recording fee'
      },
      {
        id: 'recording-mortgage',
        name: 'Recording Fee - Mortgage',
        amount: 125,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.TAXES_GOV,
        editable: true,
        description: 'County recording fee'
      },
      {
        id: 'transfer-tax',
        name: 'Transfer Tax',
        amount: 2500,
        payer: FEE_PAYER.SELLER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.TAXES_GOV,
        editable: true,
        description: 'State/local transfer tax'
      }
    ],
    
    // E. Prepaids
    prepaids: [
      {
        id: 'homeowners-insurance',
        name: 'Homeowners Insurance Premium',
        amount: 1200,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.PREPAIDS,
        editable: true,
        description: '12 months'
      },
      {
        id: 'mortgage-insurance',
        name: 'Mortgage Insurance Premium',
        amount: 450,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.PREPAIDS,
        editable: true,
        description: '2 months'
      },
      {
        id: 'prepaid-interest',
        name: 'Prepaid Interest',
        amount: 1875,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.PREPAIDS,
        editable: true,
        description: '15 days @ $125/day'
      },
      {
        id: 'property-taxes',
        name: 'Property Taxes',
        amount: 3000,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.PREPAIDS,
        editable: true,
        description: '6 months'
      }
    ],
    
    // F. Initial Escrow Payment at Closing
    escrow: [
      {
        id: 'escrow-insurance',
        name: 'Homeowners Insurance Reserve',
        amount: 200,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.ESCROW,
        editable: true,
        description: '2 months @ $100/month'
      },
      {
        id: 'escrow-taxes',
        name: 'Property Tax Reserve',
        amount: 1000,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.LOS,
        category: FEE_CATEGORIES.ESCROW,
        editable: true,
        description: '2 months @ $500/month'
      }
    ],
    
    // G. Other
    other: [
      {
        id: 'hoa-transfer',
        name: 'HOA Transfer Fee',
        amount: 250,
        payer: FEE_PAYER.BORROWER,
        source: FEE_SOURCE.MANUAL,
        category: FEE_CATEGORIES.OTHER,
        editable: true,
        description: 'Homeowners association'
      }
    ]
  };
  
  return {
    fees,
    loanAmount,
    propertyValue,
    downPayment,
    version: 1,
    lastUpdated: new Date().toISOString(),
    source: 'Generated'
  };
};

/**
 * Pulls fees from LOS (mock implementation)
 * @param {number} loanId - Loan ID
 * @returns {Promise<Object>} Fee worksheet from LOS
 */
export const pullFeesFromLOS = async (loanId) => {
  // Mock LOS integration - will later call real LOS API
  return new Promise((resolve) => {
    setTimeout(() => {
      const loanData = { loanAmount: 450000, propertyValue: 500000 };
      const worksheet = generateMockFeeWorksheet(loanData);
      
      resolve({
        ...worksheet,
        source: 'LOS',
        pulledAt: new Date().toISOString(),
        pulledBy: 'Current User',
        losSystem: 'Vesta'
      });
    }, 1500);
  });
};

/**
 * Saves fee worksheet
 * @param {number} loanId - Loan ID
 * @param {Object} worksheet - Fee worksheet
 * @returns {Promise<Object>} Saved worksheet
 */
export const saveFeeWorksheet = async (loanId, worksheet) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        ...worksheet,
        version: (worksheet.version || 0) + 1,
        lastUpdated: new Date().toISOString(),
        savedBy: 'Current User'
      });
    }, 500);
  });
};

/**
 * Calculates cash to close
 * @param {Object} worksheet - Fee worksheet
 * @returns {Object} Cash to close breakdown
 */
export const calculateCashToClose = (worksheet) => {
  const { fees, downPayment, loanAmount } = worksheet;
  
  // Sum all fees by payer
  const borrowerFees = [];
  const lenderCredits = [];
  const sellerCredits = [];
  
  Object.values(fees).flat().forEach(fee => {
    if (fee.payer === FEE_PAYER.BORROWER) {
      borrowerFees.push(fee);
    } else if (fee.payer === FEE_PAYER.LENDER) {
      lenderCredits.push(fee);
    } else if (fee.payer === FEE_PAYER.SELLER) {
      sellerCredits.push(fee);
    }
  });
  
  const totalBorrowerFees = borrowerFees.reduce((sum, fee) => sum + fee.amount, 0);
  const totalLenderCredits = lenderCredits.reduce((sum, fee) => sum + fee.amount, 0);
  const totalSellerCredits = sellerCredits.reduce((sum, fee) => sum + fee.amount, 0);
  
  const closingCosts = totalBorrowerFees;
  const credits = totalLenderCredits + totalSellerCredits;
  const cashToClose = downPayment + closingCosts - credits;
  
  return {
    downPayment,
    closingCosts,
    lenderCredits: totalLenderCredits,
    sellerCredits: totalSellerCredits,
    totalCredits: credits,
    cashToClose,
    breakdown: {
      borrowerFees,
      lenderCredits,
      sellerCredits
    }
  };
};

/**
 * Calculates broker compensation summary
 * @param {Object} worksheet - Fee worksheet
 * @returns {Object} Broker compensation details
 */
export const calculateBrokerCompensation = (worksheet) => {
  const { fees } = worksheet;
  
  const brokerFees = Object.values(fees)
    .flat()
    .filter(fee => fee.id === 'broker-comp' || fee.name.toLowerCase().includes('broker'));
  
  const totalCompensation = brokerFees.reduce((sum, fee) => sum + fee.amount, 0);
  const paidByLender = brokerFees.filter(f => f.payer === FEE_PAYER.LENDER).reduce((sum, fee) => sum + fee.amount, 0);
  const paidByBorrower = brokerFees.filter(f => f.payer === FEE_PAYER.BORROWER).reduce((sum, fee) => sum + fee.amount, 0);
  
  return {
    totalCompensation,
    paidByLender,
    paidByBorrower,
    calculation: brokerFees[0]?.description || 'Not specified',
    fees: brokerFees
  };
};

/**
 * Validates fee data
 * @param {Object} fee - Fee object
 * @returns {Object} Validation result
 */
export const validateFee = (fee) => {
  const errors = {};
  
  if (!fee.name || fee.name.trim() === '') {
    errors.name = 'Fee name is required';
  }
  
  if (fee.amount === null || fee.amount === undefined) {
    errors.amount = 'Fee amount is required';
  } else if (isNaN(fee.amount)) {
    errors.amount = 'Fee amount must be a number';
  } else if (fee.amount < 0) {
    errors.amount = 'Fee amount cannot be negative';
  }
  
  if (!fee.payer) {
    errors.payer = 'Fee payer is required';
  }
  
  if (!fee.category) {
    errors.category = 'Fee category is required';
  }
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Adds a new fee to worksheet
 * @param {Object} worksheet - Current worksheet
 * @param {string} section - Section key (origination, cannotShop, etc.)
 * @param {Object} newFee - New fee object
 * @returns {Object} Updated worksheet
 */
export const addFee = (worksheet, section, newFee) => {
  const validation = validateFee(newFee);
  if (!validation.valid) {
    throw new Error(Object.values(validation.errors).join(', '));
  }
  
  const updatedWorksheet = { ...worksheet };
  updatedWorksheet.fees = { ...worksheet.fees };
  updatedWorksheet.fees[section] = [...(worksheet.fees[section] || []), {
    ...newFee,
    id: `fee-${Date.now()}`,
    source: FEE_SOURCE.MANUAL
  }];
  
  return updatedWorksheet;
};

/**
 * Updates an existing fee
 * @param {Object} worksheet - Current worksheet
 * @param {string} section - Section key
 * @param {string} feeId - Fee ID
 * @param {Object} updates - Fee updates
 * @returns {Object} Updated worksheet
 */
export const updateFee = (worksheet, section, feeId, updates) => {
  const updatedWorksheet = { ...worksheet };
  updatedWorksheet.fees = { ...worksheet.fees };
  updatedWorksheet.fees[section] = worksheet.fees[section].map(fee =>
    fee.id === feeId ? { ...fee, ...updates } : fee
  );
  
  return updatedWorksheet;
};

/**
 * Removes a fee from worksheet
 * @param {Object} worksheet - Current worksheet
 * @param {string} section - Section key
 * @param {string} feeId - Fee ID
 * @returns {Object} Updated worksheet
 */
export const removeFee = (worksheet, section, feeId) => {
  const updatedWorksheet = { ...worksheet };
  updatedWorksheet.fees = { ...worksheet.fees };
  updatedWorksheet.fees[section] = worksheet.fees[section].filter(fee => fee.id !== feeId);
  
  return updatedWorksheet;
};

/**
 * Gets fee warnings
 * @param {Object} worksheet - Fee worksheet
 * @returns {Array} Array of warnings
 */
export const getFeeWarnings = (worksheet) => {
  const warnings = [];
  
  Object.entries(worksheet.fees).forEach(([section, feeList]) => {
    feeList.forEach(fee => {
      if (fee.amount === 0) {
        warnings.push({
          type: 'zero-amount',
          message: `${fee.name} has zero amount`,
          fee: fee
        });
      }
      
      if (fee.source === FEE_SOURCE.MANUAL && !fee.description) {
        warnings.push({
          type: 'missing-description',
          message: `${fee.name} is missing description`,
          fee: fee
        });
      }
    });
  });
  
  return warnings;
};
